import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { tripService } from '../../services/tripService';
import { inventoryService } from '../../services/inventoryService';

import { useNavigate, useLocation } from 'react-router-dom';

import Header from '../../components/navigation/Header';
import MobileNavigation from '../../components/navigation/MobileNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CategorySection from './components/CategorySection';
import AddItemForm from './components/AddItemForm';
import InventoryStats from './components/InventoryStats';
import SearchAndFilter from './components/SearchAndFilter';
import BulkActions from './components/BulkActions';
import Modal from '../../components/ui/Modal';


const InventoryManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [expandedCategories, setExpandedCategories] = useState({
    Fresh: true,
    Refrigerated: true,
    Proteins: true,
    Canned: true,
    Dry: true,
    Snack: true,
    Condiment: true,
    Beverage: true,
    Other: true
  });

  const [editingItem, setEditingItem] = useState(null);

  const categories = ['Fresh', 'Refrigerated', 'Proteins', 'Canned', 'Dry', 'Snack', 'Condiment', 'Beverage', 'Other'];

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        // Check if we just created/updated a trip
        const tripCreated = location?.state?.tripCreated;
        const tripDataFromNav = location?.state?.tripData;
        
        let tripData = null;
        
        // If trip was just created, use the data from navigation state first
        if (tripCreated && tripDataFromNav) {
          tripData = tripDataFromNav;
          setTrip(tripData);
          
          // Then fetch fresh data from database to ensure sync
          const tripResult = await tripService?.getActiveTrip(user?.id);
          if (tripResult?.data) {
            setTrip(tripResult?.data);
          }
        } else {
          // Normal flow - fetch trip data
          const tripResult = await tripService?.getActiveTrip(user?.id);
          tripData = tripResult?.data;
          setTrip(tripData);
        }
        
        // Load inventory items - use correct method with trip ID
        if (tripData?.id) {
          const inventoryResult = await inventoryService?.getInventoryByTrip(tripData?.id);
          setInventoryItems(inventoryResult?.data || []);
        }
        
        // Clear navigation state after using it
        if (tripCreated) {
          window.history?.replaceState({}, document.title);
        }
      } catch (e) {
        setError(e?.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, location?.state]);

  const filteredItems = useMemo(() => {
    return inventoryItems?.filter(item => {
      const matchesSearch = item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || !selectedCategory || item?.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [inventoryItems, searchQuery, selectedCategory]);

  const groupedItems = useMemo(() => {
    const grouped = {};
    categories?.forEach(category => {
      grouped[category] = filteredItems?.filter(item => item?.category === category);
    });
    return grouped;
  }, [filteredItems]);

  const stats = useMemo(() => {
    const totalItems = inventoryItems?.length;
    const totalCategories = categories?.filter(cat => 
      inventoryItems?.some(item => item?.category === cat)
    )?.length;
    const lowStockItems = inventoryItems?.filter(item => item?.quantity < 3)?.length;
    const totalUnits = inventoryItems?.reduce((sum, item) => sum + item?.quantity, 0);

    return {
      totalItems,
      totalCategories,
      lowStockItems,
      totalUnits: Math.round(totalUnits)
    };
  }, [inventoryItems]);

  const lowStockItemsList = useMemo(() => {
    return inventoryItems?.filter(item => item?.quantity < 3) || [];
  }, [inventoryItems]);

  const handleAddItem = async (itemData) => {
    if (!trip) return;

    if (editingItem) {
      const { data, error } = await inventoryService?.updateInventoryItem(editingItem?.id, itemData);
      if (!error && data) {
        setInventoryItems(prev =>
          prev?.map(item => item?.id === editingItem?.id ? data : item)
        );
        setEditingItem(null);
      }
    } else {
      const { data, error } = await inventoryService?.createInventoryItem(trip?.id, itemData);
      if (!error && data) {
        setInventoryItems(prev => [...prev, data]);
      }
    }
    setShowAddForm(false);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      const { error } = await inventoryService?.deleteInventoryItem(itemId);
      if (!error) {
        setInventoryItems(prev => prev?.filter(item => item?.id !== itemId));
        setSelectedItems(prev => prev?.filter(id => id !== itemId));
      }
    }
  };

  const handleQuantityAdjust = async (itemId, amount) => {
    const item = inventoryItems?.find(i => i?.id === itemId);
    if (!item) return;

    const newQuantity = Math.max(0, item?.quantity + amount);
    const updatedData = {
      itemName: item?.name,
      category: item?.category,
      quantity: newQuantity,
      unit: item?.unit,
      lastUsed: amount < 0 ? new Date()?.toISOString()?.split('T')?.[0] : item?.lastUsed
    };

    const { data, error } = await inventoryService?.updateInventoryItem(itemId, updatedData);
    if (!error && data) {
      setInventoryItems(prev =>
        prev?.map(i => i?.id === itemId ? data : i)
      );
    }
  };

  const handleToggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev?.[category]
    }));
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Delete ${selectedItems?.length} selected items?`)) {
      const { error } = await inventoryService?.bulkDeleteInventoryItems(selectedItems);
      if (!error) {
        setInventoryItems(prev => prev?.filter(item => !selectedItems?.includes(item?.id)));
        setSelectedItems([]);
      }
    }
  };

  const handleBulkAdjust = async (amount) => {
    const updates = selectedItems?.map(async (itemId) => {
      const item = inventoryItems?.find(i => i?.id === itemId);
      if (!item) return null;

      const newQuantity = Math.max(0, item?.quantity + amount);
      const updatedData = {
        itemName: item?.name,
        category: item?.category,
        quantity: newQuantity,
        unit: item?.unit,
        lastUsed: amount < 0 ? new Date()?.toISOString()?.split('T')?.[0] : item?.lastUsed
      };

      return inventoryService?.updateInventoryItem(itemId, updatedData);
    });

    await Promise.all(updates);
    
    const { data: items } = await inventoryService?.getInventoryByTrip(trip?.id);
    setInventoryItems(items || []);
    setSelectedItems([]);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setShowAddForm(false);
  };

  const handleDeleteAll = () => {
    setShowDeleteAllModal(true);
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteAllModal(false);
  };

  const handleConfirmDeleteAll = async () => {
    setIsDeleting(true);
    const allItemIds = inventoryItems?.map(item => item?.id);
    const { error } = await inventoryService?.bulkDeleteInventoryItems(allItemIds);
    if (!error) {
      setInventoryItems([]);
      setSelectedItems([]);
    }
    setIsDeleting(false);
    setShowDeleteAllModal(false);
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Inventory Management - GalleyPlan</title>
        </Helmet>
        <Header />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">Loading inventory...</p>
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  if (!trip) {
    return (
      <>
        <Helmet>
          <title>Inventory Management - GalleyPlan</title>
        </Helmet>
        <Header />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Ship" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No Active Trip</h2>
            <p className="text-muted-foreground mb-6">Create a trip first to manage inventory</p>
            <Button variant="default" onClick={() => navigate('/trip-setup')}>
              Create Trip
            </Button>
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Inventory Management - GalleyPlan</title>
        <meta 
          name="description" 
          content="Manage your coastal cruiser provisions with comprehensive inventory tracking and quantity monitoring" 
        />
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background pt-16 pb-20 lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Inventory Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Track and manage your onboard provisions
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              onClick={() => setShowAddForm(!showAddForm)}
              iconName={showAddForm ? "X" : "Plus"}
              iconPosition="left"
              className="hidden lg:flex"
            >
              {showAddForm ? 'Close Form' : 'Add Item'}
            </Button>
          </div>

          {/* Mobile: Always visible Add Item Form */}
          <div className="lg:hidden mb-6">
            <AddItemForm
              onAdd={handleAddItem}
              editingItem={editingItem}
              onCancelEdit={handleCancelEdit}
            />
          </div>

          <InventoryStats stats={stats} lowStockItems={lowStockItemsList} />

          <div className="mt-6 md:mt-8">
            <SearchAndFilter
              searchTerm={searchQuery}
              onSearchChange={setSearchQuery}
              filterCategory={selectedCategory}
              onFilterChange={setSelectedCategory}
              onDeleteAll={handleDeleteAll}
            />
          </div>

          {selectedItems?.length > 0 && (
            <div className="mt-6">
              <BulkActions
                selectedItems={selectedItems}
                onBulkDelete={handleBulkDelete}
                onBulkAdjust={handleBulkAdjust}
                onClearSelection={() => setSelectedItems([])}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mt-6 md:mt-8">
            <div className="lg:col-span-2 space-y-4 md:space-y-6">
              {categories?.map(category => (
                <CategorySection
                  key={category}
                  category={category}
                  items={groupedItems?.[category]}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                  onQuantityAdjust={handleQuantityAdjust}
                  isExpanded={expandedCategories?.[category]}
                  onToggle={() => handleToggleCategory(category)}
                />
              ))}

              {filteredItems?.length === 0 && (
                <div className="bg-card border border-border rounded-lg p-8 md:p-12 text-center">
                  <Icon 
                    name="Package" 
                    size={64} 
                    className="text-muted-foreground mx-auto mb-4"
                  />
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                    No items found
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-6">
                    {searchQuery || selectedCategory 
                      ? 'Try adjusting your search or filter criteria' :'Start by adding your first inventory item'}
                  </p>
                  {!searchQuery && !selectedCategory && (
                    <Button
                      variant="default"
                      onClick={() => setShowAddForm(true)}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Add First Item
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className={`lg:sticky lg:top-24 lg:self-start ${showAddForm ? 'block' : 'hidden lg:block'}`}>
              <AddItemForm
                onAdd={handleAddItem}
                editingItem={editingItem}
                onCancelEdit={handleCancelEdit}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showDeleteAllModal}
        onClose={handleCancelDeleteAll}
        title="Delete All Inventory"
        showCloseButton={false}
      >
        <div className="space-y-6">
          <p className="text-text-secondary">
            Are you sure you want to Delete All Inventory?
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={handleCancelDeleteAll}
              disabled={isDeleting}
            >
              No
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteAll}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Yes'}
            </Button>
          </div>
        </div>
      </Modal>

      <MobileNavigation />
    </>
  );
};

export default InventoryManagement;